import React, { useState } from "react";
import type { BillingDocument, DocumentStatus } from "../../types";
import { formatDate, money } from "../../lib/utils";
import { printDocument } from "../../lib/pdf";
import { useBilling } from "../../store/BillingContext";

interface DocumentTableProps {
  docs: BillingDocument[];
  onEdit: (doc: BillingDocument) => void;
  showConvert?: boolean;
}

export const DocumentTable: React.FC<DocumentTableProps> = ({
  docs,
  onEdit,
  showConvert = false,
}) => {
  const { deleteDocument, convertQuoteToInvoice, updateDocumentStatus, state, setPreviewDoc } = useBilling();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filteredDocs = docs.filter((d) => {
    const matchesSearch =
      (d.number || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.company || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.email || "").toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ? true : d.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const statuses: { label: string; value: string }[] = [
    { label: "All", value: "all" },
    { label: "Sent", value: "sent" },
    { label: "Paid", value: "paid" },
    { label: "Draft", value: "draft" },
    { label: "Overdue", value: "overdue" },
    { label: "Accepted", value: "accepted" },
    { label: "Cancelled", value: "cancelled" },
  ];

  return (
    <div className="document-table-container">
      {/* Search and Status Toolbar */}
      <div className="toolbar" style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center", marginBottom: "16px" }}>
        <div style={{ position: "relative", flex: 1, minWidth: "220px" }}>
          <i
            className="fa-solid fa-magnifying-glass"
            style={{
              position: "absolute",
              left: "14px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--text-faint)",
              fontSize: "13px",
            }}
          />
          <input
            type="text"
            placeholder="Search invoice #, client, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ paddingLeft: "38px" }}
          />
        </div>

        <div className="filter-pills-scroll" style={{ display: "flex", gap: "6px", overflowX: "auto", maxWidth: "100%", paddingBottom: "4px" }}>
          {statuses.map((s) => {
            const isSelected = statusFilter === s.value;
            const count =
              s.value === "all"
                ? docs.length
                : docs.filter((d) => d.status === s.value).length;

            return (
              <button
                key={s.value}
                onClick={() => setStatusFilter(s.value)}
                style={{
                  padding: "5px 10px",
                  fontSize: "11.5px",
                  borderRadius: "var(--radius-full)",
                  border: "1px solid",
                  borderColor: isSelected ? "var(--brand-navy)" : "var(--border-subtle)",
                  background: isSelected ? "var(--brand-navy)" : "#ffffff",
                  color: isSelected ? "#ffffff" : "var(--text-muted)",
                  fontWeight: isSelected ? 600 : 500,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <span>{s.label}</span>
                {count > 0 && (
                  <span
                    style={{
                      fontSize: "10px",
                      background: isSelected ? "rgba(255,255,255,0.2)" : "#f1f5f9",
                      padding: "1px 5px",
                      borderRadius: "10px",
                      color: isSelected ? "#ffffff" : "var(--text-muted)",
                      fontWeight: 600,
                    }}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* 1. Desktop Table View (visible on desktop & tablets) */}
      <div className="desktop-table-view table-wrap">
        <table style={{ minWidth: "960px" }}>
          <thead>
            <tr>
              <th>Document #</th>
              <th>Client / Company</th>
              <th>Issue Date</th>
              <th>Due Date</th>
              <th>Status</th>
              <th style={{ textAlign: "right" }}>Total Amount</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDocs.length > 0 ? (
              filteredDocs.map((d) => (
                <tr key={d.id}>
                  <td>
                    <div
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontWeight: 700,
                        fontSize: "13px",
                        color: "var(--brand-navy)",
                        background: "#f1f5f9",
                        border: "1px solid var(--border-subtle)",
                        padding: "3px 8px",
                        borderRadius: "var(--radius-sm)",
                        display: "inline-block",
                      }}
                    >
                      {d.number}
                    </div>
                    {d.sourceQuoteId && (
                      <div style={{ fontSize: "10px", color: "var(--text-faint)", marginTop: "2px" }}>
                        From Quote
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, color: "var(--text-main)" }}>
                      {d.company || "—"}
                    </div>
                    {d.email && (
                      <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{d.email}</div>
                    )}
                  </td>
                  <td style={{ color: "var(--text-muted)", fontSize: "12.5px" }}>
                    {formatDate(d.date)}
                  </td>
                  <td style={{ color: "var(--text-muted)", fontSize: "12.5px" }}>
                    {formatDate(d.due)}
                  </td>
                  <td>
                    <select
                      value={d.status}
                      onChange={(e) =>
                        updateDocumentStatus(d.id, d.type, e.target.value as DocumentStatus)
                      }
                      style={{
                        padding: "4px 10px",
                        fontSize: "11px",
                        borderRadius: "var(--radius-full)",
                        border: "1px solid var(--border-subtle)",
                        background:
                          d.status === "paid"
                            ? "var(--status-paid-bg)"
                            : d.status === "sent"
                            ? "var(--status-sent-bg)"
                            : "var(--status-draft-bg)",
                        color:
                          d.status === "paid"
                            ? "var(--status-paid-text)"
                            : d.status === "sent"
                            ? "var(--status-sent-text)"
                            : "var(--status-draft-text)",
                        fontWeight: 600,
                        cursor: "pointer",
                        width: "auto",
                      }}
                    >
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="paid">Paid</option>
                      <option value="accepted">Accepted</option>
                      <option value="declined">Declined</option>
                      <option value="overdue">Overdue</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      fontWeight: 700,
                      fontSize: "14px",
                      color: "var(--text-main)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {money(d.total)}
                  </td>
                  <td className="row-actions">
                    <button
                      onClick={() => onEdit(d)}
                      title="Edit Document"
                      style={{ color: "#2563eb" }}
                    >
                      <i className="fa-solid fa-pen-to-square" /> Edit
                    </button>
                    <button
                      onClick={() => setPreviewDoc(d)}
                      title="Preview on Screen"
                      style={{ color: "#059669" }}
                    >
                      <i className="fa-solid fa-eye" /> Preview
                    </button>
                    <button
                      onClick={() => printDocument(d, state)}
                      title="Print or Export to PDF"
                      style={{ color: "#7c3aed" }}
                    >
                      <i className="fa-solid fa-print" /> PDF
                    </button>
                    {showConvert && (
                      <button
                        className="gold"
                        onClick={() => convertQuoteToInvoice(d)}
                        title="Convert this quote into an invoice"
                        style={{ padding: "4px 8px", fontSize: "11px" }}
                      >
                        <i className="fa-solid fa-arrow-right-arrow-left" /> Invoice
                      </button>
                    )}
                    <button
                      className="danger"
                      onClick={() => {
                        if (confirm(`Are you sure you want to delete ${d.number}?`)) {
                          deleteDocument(d);
                        }
                      }}
                      title="Delete Document"
                    >
                      <i className="fa-solid fa-trash" />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="empty">
                  <div style={{ padding: "36px 0" }}>
                    <i
                      className="fa-solid fa-folder-open"
                      style={{ fontSize: "32px", color: "var(--border-strong)", marginBottom: "12px" }}
                    />
                    <p style={{ margin: 0, fontWeight: 500 }}>No documents match the current filter.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 2. Mobile Responsive Card List View (visible on phones) */}
      <div className="mobile-cards-view">
        {filteredDocs.length > 0 ? (
          filteredDocs.map((d) => (
            <div key={d.id} className="mobile-doc-card">
              <div className="mobile-doc-top">
                <span className="mobile-doc-num">{d.number}</span>
                <select
                  value={d.status}
                  onChange={(e) =>
                    updateDocumentStatus(d.id, d.type, e.target.value as DocumentStatus)
                  }
                  className={`mobile-doc-status status-${d.status}`}
                >
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                  <option value="accepted">Accepted</option>
                  <option value="declined">Declined</option>
                  <option value="overdue">Overdue</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="mobile-doc-company">
                {d.company || "Unnamed Client"}
              </div>
              {d.email && <div className="mobile-doc-email">{d.email}</div>}

              <div className="mobile-doc-dates">
                <span>Issue: {formatDate(d.date)}</span>
                <span>Due: {formatDate(d.due)}</span>
              </div>

              <div className="mobile-doc-amount">
                <small>Total Amount</small>
                <strong>{money(d.total)}</strong>
              </div>

              <div className="mobile-doc-actions">
                <button onClick={() => onEdit(d)}>
                  <i className="fa-solid fa-pen-to-square" /> Edit
                </button>
                <button onClick={() => setPreviewDoc(d)}>
                  <i className="fa-solid fa-eye" /> Preview
                </button>
                <button onClick={() => printDocument(d, state)}>
                  <i className="fa-solid fa-print" /> PDF
                </button>
                {showConvert && (
                  <button className="gold" onClick={() => convertQuoteToInvoice(d)}>
                    <i className="fa-solid fa-arrow-right-arrow-left" /> Inv
                  </button>
                )}
                <button
                  className="danger"
                  onClick={() => {
                    if (confirm(`Delete ${d.number}?`)) deleteDocument(d);
                  }}
                >
                  <i className="fa-solid fa-trash" />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="empty" style={{ padding: "30px 0" }}>
            No documents found.
          </div>
        )}
      </div>
    </div>
  );
};
